import styled from "styled-components";
import theme from "../../styles/theme";
import { lazy, Suspense } from "react";
import { useDriverContext } from "../../contexts/driver";
import { Dropdown } from "../Common/Dropdown";
import { SIMULATORS } from "../../utils/simulators";
import ChartSkeleton from "./Charts/ChartSkeleton";
import StatsTablesSkeleton from "./StatsTablesSkeleton";
import DriverStatsEmpty from "./DriverStatsEmpty";
import {
  StatLabel,
  StatPercentage,
  StatResult,
  StatRow,
  StatsSection,
  StatsTable,
  StatsTableHeader,
  StatsTablesContainer,
} from "./StatsTableStyles";

import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import {
  AutoAwesomeOutlined,
  LocalFireDepartmentOutlined,
  RestartAltOutlined,
  SportsScoreOutlined,
  StarOutlineOutlined,
  TimerOutlined,
  WarningAmberOutlined,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";

// recharts is heavy and sits below the fold on a non-default tab, so it loads on demand.
const DriverCharts = lazy(() => import("./Charts/DriverCharts"));

// Styled components for the main container
const StatsContainer = styled.div`
  width: 100%;
`;

// Styled components for the broad statistics cards
const BroadStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 32px;

  @media (max-width: 1300px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 650px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  position: relative;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 140px;
  border: 2px solid ${(props) => props.mainColor || theme.colors.green};
  background-color: ${(props) =>
    props.backgroundColor || `${theme.colors.green}4D`}; /* 30% opacity */
`;

// Update the StatTitle styling
const StatTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.color || props.mainColor || theme.colors.green};
  font-family: "Montserrat", sans-serif;
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 1;
`;

// Update the StatValue styling
const StatValue = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: ${(props) => props.color || props.mainColor || theme.colors.green};
  font-family: "Montserrat", sans-serif;
  margin-top: auto;
  display: flex;
  align-items: baseline;
  line-height: 1;
`;

// Update the StatSecondaryValue styling
const StatSecondaryValue = styled.span`
  font-size: 20px;
  color: ${(props) =>
    props.color || `${props.mainColor || theme.colors.green}80`}; /* 50% opacity */
  margin-left: 8px;
  font-family: "Montserrat", sans-serif;
  line-height: 1;
`;

// Update the IconContainer styling to make icons bigger
const IconContainer = styled.div`
  color: ${(props) => props.color || props.mainColor || theme.colors.green};
  display: flex;
  align-items: center;
  & .MuiSvgIcon-root {
    font-size: 48px;
  }
`;

const SIMULATOR_FILTER_OPTIONS = [
  { value: null, label: "Усі симулятори" },
  ...SIMULATORS.map((sim) => ({ value: sim.id, label: sim.name })),
];

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${theme.colors.secondaryDark};
`;

const SectionTitle = styled.h3`
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${theme.colors.white};
  margin: 0;
`;

const Section = styled.section`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DriverStats = ({ color }) => {
  const {
    driverStats,
    driverTableStats,
    isStatsLoading,
    isTableStatsLoading,
    selectedSimulatorId,
    setSelectedSimulatorId,
  } = useDriverContext();

  // The tiles read the pooled career totals; only the tables below follow the simulator dropdown.
  const getStatValue = (name) => {
    if (!driverStats) return null;
    const stat = driverStats.find((s) => s.name === name);
    return stat ? stat.value : null;
  };

  const getTableStatValue = (name) => {
    if (!driverTableStats) return null;
    const stat = driverTableStats.find((s) => s.name === name);
    return stat ? stat.value : null;
  };

  const formatStat = (value) => {
    if (value === null || value === undefined) return "-";
    return value;
  };

  const calculatePercentage = (value, total) => {
    if (!value || !total) return null;
    return ((value / total) * 100).toFixed(1);
  };

  if (isStatsLoading) {
    return <div>Завантаження статистики...</div>;
  }

  if (!driverStats) {
    return <div>Статистика недоступна</div>;
  }

  // Get individual values
  const raceStarts = getStatValue("race_starts") || 0;
  const sprintStarts = getStatValue("sprint_starts") || 0;
  const raceWins = getStatValue("race_wins") || 0;
  const sprintWins = getStatValue("sprint_wins") || 0;
  const racePodiums = getStatValue("race_podiums") || 0;
  const sprintPodiums = getStatValue("sprint_podiums") || 0;
  const raceTop5 = getStatValue("race_top5") || 0;
  const sprintTop5 = getStatValue("sprint_top5") || 0;
  const raceTop10 = getStatValue("race_top10") || 0;
  const sprintTop10 = getStatValue("sprint_top10") || 0;
  const raceFastestLaps = getStatValue("fastest_lap_count") || 0;
  const raceLaps = getStatValue("race_laps_count") || 0;
  const sprintLaps = getStatValue("sprint_laps_count") || 0;
  const qualPoles = getStatValue("qualifcation_pole_positions") || 0;
  const raceMaxPenalty = getStatValue("race_max_penalty") || 0;
  const sprintMaxPenalty = getStatValue("sprint_max_penalty") || 0;

  // Calculate combined values
  const totalStarts = raceStarts + sprintStarts;
  const totalWins = raceWins + sprintWins;
  const totalPodiums = racePodiums + sprintPodiums;
  const totalTop5 = raceTop5 + sprintTop5;
  const totalTop10 = raceTop10 + sprintTop10;
  const totalLaps = raceLaps + sprintLaps;
  const maxPenalty = Math.max(raceMaxPenalty, sprintMaxPenalty);

  // Calculate percentages based on combined values
  const winsPercentage = `${calculatePercentage(totalWins, totalStarts)}%`;
  const podiumsPercentage = `${calculatePercentage(totalPodiums, totalStarts)}%`;
  const top5Percentage = `${calculatePercentage(totalTop5, totalStarts)}%`;
  const top10Percentage = `${calculatePercentage(totalTop10, totalStarts)}%`;
  const polePercentage = `${calculatePercentage(qualPoles, getStatValue("qualifications_starts"))}%`;

  // Create an array of card data to easily filter out zero values
  const cardData = [
    {
      title: "Старти",
      value: totalStarts,
      secondaryValue: null,
      icon: <SportsScoreOutlined />,
      mainColor: "#7B7B7B",
      backgroundColor: "#2A2A2AC4",
      show: totalStarts > 0,
    },
    {
      title: "Перемоги",
      value: totalWins,
      secondaryValue: winsPercentage,
      icon: <EmojiEventsOutlinedIcon />,
      mainColor: "#FFB800",
      backgroundColor: "#FFB8004D",
      show: totalWins > 0,
    },
    {
      title: "Подіуми",
      value: totalPodiums,
      secondaryValue: podiumsPercentage,
      icon: <WorkspacePremiumOutlined />,
      mainColor: "#C0C0C0",
      backgroundColor: "#C0C0C04D",
      show: totalPodiums > 0,
    },
    {
      title: "Топ 5",
      value: totalTop5,
      secondaryValue: top5Percentage,
      icon: <StarOutlineOutlined />,
      mainColor: "#6488EA",
      backgroundColor: "#6488EA4D",
      show: totalTop5 > 0,
    },
    {
      title: "Топ 10",
      value: totalTop10,
      secondaryValue: top10Percentage,
      icon: <AutoAwesomeOutlined />,
      mainColor: "#ff8000",
      backgroundColor: "#ff80004D",
      show: totalTop10 > 0,
    },
    {
      title: "Поул позиції",
      value: qualPoles,
      secondaryValue: polePercentage,
      icon: <LocalFireDepartmentOutlined />,
      mainColor: "#FD4BC7",
      backgroundColor: "#FD4BC74D",
      show: qualPoles > 0,
    },
    {
      title: "Найшвидші кола",
      value: raceFastestLaps,
      secondaryValue: null,
      icon: <TimerOutlined />,
      mainColor: "#9932CC",
      backgroundColor: "#9932CC4D",
      show: raceFastestLaps > 0,
    },
    {
      title: "Усього кіл",
      value: totalLaps,
      secondaryValue: null,
      icon: <RestartAltOutlined />,
      mainColor: "#C6934B",
      backgroundColor: "#C6934B4D",
      show: totalLaps > 0,
    },
    {
      title: "Найбільший штраф",
      value: maxPenalty,
      secondaryValue: "сек.",
      icon: <WarningAmberOutlined />,
      mainColor: "#BD162C",
      backgroundColor: "#BD162C4D",
      show: maxPenalty > 0,
    },
  ];

  // Filter out cards with zero values
  const visibleCards = cardData.filter((card) => card.show);

  // Create qualification stats rows
  const qualificationStats = [
    {
      label: "Старти",
      value: getTableStatValue("qualifications_starts"),
    },
    {
      label: "Поул позиції",
      value: getTableStatValue("qualifcation_pole_positions"),
      percentage: calculatePercentage(
        getTableStatValue("qualifcation_pole_positions"),
        getTableStatValue("qualifications_starts"),
      ),
    },
    {
      label: "Найкращий результат",
      value: getTableStatValue("qualification_best_position"),
      visible:
        getTableStatValue("qualifcation_pole_positions") === 0 ||
        getTableStatValue("qualifcation_pole_positions") === null,
    },
    {
      label: "Топ 5",
      value: getTableStatValue("qualifcation_top5"),
      percentage: calculatePercentage(
        getTableStatValue("qualifcation_top5"),
        getTableStatValue("qualifications_starts"),
      ),
    },
    {
      label: "Топ 10",
      value: getTableStatValue("qualifcation_top10"),
      percentage: calculatePercentage(
        getTableStatValue("qualifcation_top10"),
        getTableStatValue("qualifications_starts"),
      ),
    },
    { label: "Середній результат", value: getTableStatValue("qualifcation_avg_position") },
  ].filter((stat) => {
    if (stat.value === null || stat.value === 0) return false;

    if (stat.hasOwnProperty("visible") && stat.visible === false) return false;

    return true;
  });

  // Create sprint stats rows
  const sprintStats = [
    { label: "Старти", value: getTableStatValue("sprint_starts") },
    {
      label: "Перемоги",
      value: getTableStatValue("sprint_wins"),
      percentage: calculatePercentage(
        getTableStatValue("sprint_wins"),
        getTableStatValue("sprint_starts"),
      ),
    },
    {
      label: "Подіуми",
      value: getTableStatValue("sprint_podiums"),
      percentage: calculatePercentage(
        getTableStatValue("sprint_podiums"),
        getTableStatValue("sprint_starts"),
      ),
    },
    {
      label: "Топ 5",
      value: getTableStatValue("sprint_top5"),
      percentage: calculatePercentage(
        getTableStatValue("sprint_top5"),
        getTableStatValue("sprint_starts"),
      ),
    },
    {
      label: "Топ 10",
      value: getTableStatValue("sprint_top10"),
      percentage: calculatePercentage(
        getTableStatValue("sprint_top10"),
        getTableStatValue("sprint_starts"),
      ),
    },
    { label: "Усього кіл", value: getTableStatValue("sprint_laps_count") },
    {
      label: "Найкраща стартова позиція",
      value: getTableStatValue("sprint_best_grid_position"),
    },
    {
      label: "Найкраща фінішна позиція",
      value: getTableStatValue("sprint_best_finish_position"),
      visible: getTableStatValue("sprint_wins") === 0 || getTableStatValue("sprint_wins") === null,
    },
    { label: "Середня стартова позиція", value: getTableStatValue("sprint_avg_grid_position") },
    { label: "Середня фінішна позиція", value: getTableStatValue("sprint_avg_finish_position") },
    { label: "Найбільший штраф", value: getTableStatValue("sprint_max_penalty"), suffix: " сек." },
    { label: "Найшвидших кіл", value: getTableStatValue("sprint_fastest_lap_count") },
  ].filter((stat) => {
    if (stat.value === null || stat.value === 0) return false;

    if (stat.hasOwnProperty("visible") && stat.visible === false) return false;

    return true;
  });

  // Create race stats rows
  const raceStats = [
    { label: "Старти", value: getTableStatValue("race_starts") },
    {
      label: "Перемоги",
      value: getTableStatValue("race_wins"),
      percentage: calculatePercentage(
        getTableStatValue("race_wins"),
        getTableStatValue("race_starts"),
      ),
    },
    {
      label: "Подіуми",
      value: getTableStatValue("race_podiums"),
      percentage: calculatePercentage(
        getTableStatValue("race_podiums"),
        getTableStatValue("race_starts"),
      ),
    },
    {
      label: "Топ 5",
      value: getTableStatValue("race_top5"),
      percentage: calculatePercentage(
        getTableStatValue("race_top5"),
        getTableStatValue("race_starts"),
      ),
    },
    {
      label: "Топ 10",
      value: getTableStatValue("race_top10"),
      percentage: calculatePercentage(
        getTableStatValue("race_top10"),
        getTableStatValue("race_starts"),
      ),
    },
    { label: "Усього кіл", value: getTableStatValue("race_laps_count") },
    {
      label: "Найкраща стартова позиція",
      value: getTableStatValue("race_best_grid_position"),
    },
    {
      label: "Найкраща фінішна позиція",
      value: getTableStatValue("race_best_finsih_position"),
      visible: getTableStatValue("race_wins") === 0 || getTableStatValue("race_wins") === null,
    },
    { label: "Середня стартова позиція", value: getTableStatValue("race_avg_grid_position") },
    { label: "Середня фінішна позиція", value: getTableStatValue("race_avg_finish_position") },
    { label: "Найбільший штраф", value: getTableStatValue("race_max_penalty"), suffix: " сек." },
    { label: "Найшвидших кіл", value: getTableStatValue("fastest_lap_count") },
  ].filter((stat) => {
    // First check if the value exists and is not zero
    if (stat.value === null || stat.value === 0) return false;

    // Then check if this stat should be visible based on other stats
    if (stat.hasOwnProperty("visible") && stat.visible === false) return false;

    return true;
  });

  // Every table filters out null/zero rows, so all three empty means the driver has no results at
  // all for the selected simulator — the case the /stats endpoint used to 500 on.
  const hasNoTableStats =
    qualificationStats.length === 0 && sprintStats.length === 0 && raceStats.length === 0;

  const selectedSimulatorLabel =
    SIMULATORS.find((sim) => sim.id === selectedSimulatorId)?.name || "жодному симуляторі";

  return (
    <StatsContainer>
      {/* Section 1 — career totals across every simulator; no heading, the tiles label themselves */}
      <BroadStatsGrid>
        {visibleCards.map((card, index) => (
          <StatCard key={index} mainColor={card.mainColor} backgroundColor={card.backgroundColor}>
            <StatTitle mainColor={card.mainColor}>
              {card.title}
              <IconContainer mainColor={card.mainColor}>{card.icon}</IconContainer>
            </StatTitle>
            <StatValue mainColor={card.mainColor}>
              {card.value}
              {card.secondaryValue && (
                <StatSecondaryValue mainColor={card.mainColor}>
                  {typeof card.secondaryValue === "string"
                    ? card.secondaryValue
                    : `${card.secondaryValue}%`}
                </StatSecondaryValue>
              )}
            </StatValue>
          </StatCard>
        ))}
      </BroadStatsGrid>

      {/* Section 2 — always the full career, every simulator */}
      <Section>
        <SectionHeader>
          <SectionTitle>Графіки</SectionTitle>
        </SectionHeader>
        <Suspense fallback={<ChartSkeleton />}>
          <DriverCharts color={color} />
        </Suspense>
      </Section>

      {/* Section 3 — the only part the simulator dropdown scopes */}
      <Section>
        <SectionHeader>
          <SectionTitle>Загальна статистика</SectionTitle>
          <Dropdown
            label="Симулятор"
            value={selectedSimulatorId}
            options={SIMULATOR_FILTER_OPTIONS}
            onChange={setSelectedSimulatorId}
          />
        </SectionHeader>
        {isTableStatsLoading ? (
          <StatsTablesSkeleton />
        ) : hasNoTableStats ? (
          <DriverStatsEmpty
            color={color}
            simulatorLabel={selectedSimulatorLabel}
            onReset={selectedSimulatorId ? () => setSelectedSimulatorId(null) : null}
          />
        ) : (
          <StatsSection>
            <StatsTablesContainer>
              {qualificationStats.length > 0 && (
                <StatsTable>
                  <StatsTableHeader>Кваліфікація</StatsTableHeader>
                  {qualificationStats.map((stat, index) => (
                    <StatRow key={index}>
                      <StatLabel>{stat.label}</StatLabel>
                      <StatResult color={color}>
                        {formatStat(stat.value)}
                        {stat.percentage && (
                          <StatPercentage>{`(${stat.percentage}%)`}</StatPercentage>
                        )}
                        {stat.suffix}
                      </StatResult>
                    </StatRow>
                  ))}
                </StatsTable>
              )}

              {sprintStats.length > 0 && (
                <StatsTable>
                  <StatsTableHeader>Спринт</StatsTableHeader>
                  {sprintStats.map((stat, index) => (
                    <StatRow key={index}>
                      <StatLabel>{stat.label}</StatLabel>
                      <StatResult color={color}>
                        {formatStat(stat.value)}
                        {stat.percentage && (
                          <StatPercentage>{`(${stat.percentage}%)`}</StatPercentage>
                        )}
                        {stat.suffix}
                      </StatResult>
                    </StatRow>
                  ))}
                </StatsTable>
              )}

              {raceStats.length > 0 && (
                <StatsTable>
                  <StatsTableHeader>Гонка</StatsTableHeader>
                  {raceStats.map((stat, index) => (
                    <StatRow key={index}>
                      <StatLabel>{stat.label}</StatLabel>
                      <StatResult color={color}>
                        {formatStat(stat.value)}
                        {stat.percentage && (
                          <StatPercentage>{`(${stat.percentage}%)`}</StatPercentage>
                        )}
                        {stat.suffix}
                      </StatResult>
                    </StatRow>
                  ))}
                </StatsTable>
              )}
            </StatsTablesContainer>
          </StatsSection>
        )}
      </Section>
    </StatsContainer>
  );
};

export default DriverStats;
